import styled from '@emotion/styled'
import { Box, Tooltip, Typography } from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ClearIcon from '@material-ui/icons/Clear'
import useId from '@mui/material/utils/useId'
import Compressor from '@uppy/compressor'
import Uppy, { FilesAddedCallback, SuccessResponse, UppyFile } from '@uppy/core'
import '@uppy/core/dist/style.css'
import '@uppy/core/dist/style.min.css'
import Dashboard from '@uppy/dashboard'
import '@uppy/dashboard/dist/style.min.css'
import ThumbnailGenerator from '@uppy/thumbnail-generator'
import Tus from '@uppy/tus'
import Webcam from '@uppy/webcam'
import '@uppy/webcam/dist/style.min.css'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { ALLOWED_FILE_TYPES, MAX_IMAGE_SIZE_MB } from '@/constants'
import { Context } from '@/context'
import { useShowConfirmPopup } from '@/hooks'
import PDFIcon from '@/images/pdf.png'
import DeleteIcon from '@/images/trashIcon.svg'
import { FileTusMetadataArgs } from '@/schemaTypes'

export type UppyFileObject = UppyFile

interface UploaderProps {
  idPrefix?: string
  label?: string
  multiple?: boolean
  initialFiles: FileTusMetadataArgs[] | null
  allowedFileTypes?: Array<string>
  checkIcon?: 'success' | 'failure'
  disableUploadButton?: boolean
  shouldNotConfirmBeforeRemove?: boolean
  /** false: files that are already stored cannot be removed, only new uploads. */
  canRemoveStoredFiles?: boolean
  /** Single-file mode only: keep the upload button and let a new upload replace the stored file. */
  replaceOnUpload?: boolean
  compress?: boolean
  onFilesChanged?: (result: UppyFileChangeResults[]) => void
  onOpenGalery?: (
    files: Array<{ url: string; type: string }>,
    fileIndex: number,
  ) => void
  hideFileName?: boolean
}

export type FileArgs = FileTusMetadataArgs | UppyFileObject
export type UppyFileChangeResults =
  | { newFile: FileTusMetadataArgs }
  | { deletedFileIndex: number }

export const UppyUploader = ({
  label,
  idPrefix,
  initialFiles,
  onFilesChanged,
  multiple,
  allowedFileTypes = ALLOWED_FILE_TYPES,
  checkIcon,
  shouldNotConfirmBeforeRemove,
  onOpenGalery,
  disableUploadButton,
  compress = true,
  hideFileName,
  canRemoveStoredFiles = true,
  replaceOnUpload = false,
}: UploaderProps) => {
  const { t } = useTranslation()
  const { closeConfirmModal } = useContext(Context)
  const uploadingNumber = useRef(0)
  const uploadingCount = useRef(0)
  const uploadingFiles = useRef<UppyFileChangeResults[]>([])

  const maxNumberOfFiles = multiple ? null : 1
  const uppyId = useId()
  const [{ uppy, uppyDashboard }] = useState(() => {
    const uppy = new Uppy({
      id: uppyId,
      autoProceed: true,
      restrictions: {
        maxFileSize: MAX_IMAGE_SIZE_MB * 1_000_000,
        allowedFileTypes,
        maxNumberOfFiles,
      },
    })

    if (compress) {
      uppy.use(Compressor)
    }

    uppy
      .use(Dashboard, {
        showSelectedFiles: false,
        closeAfterFinish: true,
        hideUploadButton: true,
      })
      .use(Tus, {
        endpoint: process.env.REACT_APP_TUS_SERVER_URL,
        retryDelays: [0, 1000, 3000, 5000],
      })
      .use(ThumbnailGenerator, {
        id: 'ThumbnailGenerator',
      })
    const uppyDashboard = uppy.getPlugin('Dashboard') as Dashboard

    uppy.use(Webcam, {
      target: uppyDashboard,
      modes: ['picture'],
      preferredImageMimeType: 'image/jpeg',
    })

    return { uppy, uppyDashboard }
  })

  useEffect(() => {
    uppy.setOptions({
      restrictions: {
        maxNumberOfFiles,
      },
    })
  }, [maxNumberOfFiles, uppy])

  const showConfirmPopup = useShowConfirmPopup({
    actionText: t('delete'),
    title: t('confirm_delete'),
  })

  const [files, setFiles] = useState<Array<FileArgs>>([])

  useEffect(() => {
    if (initialFiles?.length) {
      setFiles(initialFiles)
    }
    // TODO: CQI-2 fix this violation of react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onUppyFileChanges = useCallback(
    (uppyFile: UppyFileObject, res?: SuccessResponse) => {
      const newFile = uppy.getFile(uppyFile.id)
      console.log(uppyFile, newFile, res)
      setFiles((prev: FileArgs[]) => {
        const next = [...prev]
        const updatedFileIndex = next.findIndex(
          (file) => isUppyFile(file) && file.id === uppyFile.id,
        )

        const prevFile = next[updatedFileIndex]

        if (!prevFile?.response?.uploadURL && newFile.response?.uploadURL) {
          const newData = {
            newFile: {
              fileId:
                newFile.response.uploadURL.replace(
                  process.env.REACT_APP_TUS_SERVER_URL + '/uploads/',
                  '',
                ) ?? null,
              filename: newFile.name,
              url: getUppyFilePreview(newFile),
            },
          }

          // handle finish multiple files
          uploadingFiles.current.push(newData)
          uploadingCount.current += 1
          if (uploadingCount.current === uploadingNumber.current) {
            onFilesChanged?.(uploadingFiles.current)
            // reset flags
            uploadingFiles.current = []
            uploadingCount.current = 0
            uploadingNumber.current = 0
          }
        }

        if (updatedFileIndex !== -1) {
          next[updatedFileIndex] = newFile
        } else if (replaceOnUpload && !multiple) {
          // The new upload takes the place of the stored file.
          return [...next.filter(isUppyFile), newFile]
        } else {
          return [...next, newFile]
        }

        return next
      })
    },
    [uppy, onFilesChanged, replaceOnUpload, multiple],
  )

  const onFileRemoved = (file: FileArgs, index: number) => {
    if (isUppyFile(file)) {
      uppy.removeFile(file.id)
    }

    setFiles((prev) => prev.filter((_, i) => i !== index))
    onFilesChanged([{ deletedFileIndex: index }])
  }

  useEffect(() => {
    const handleFilesAdded: FilesAddedCallback<Record<string, unknown>> = (
      files,
    ) => {
      uploadingNumber.current = files.length
      uploadingCount.current = 0
    }
    uppy.on('files-added', handleFilesAdded)

    const onTnGenerated = (file: UppyFileObject) => onUppyFileChanges(file)
    uppy.on('thumbnail:generated', onTnGenerated)
    uppy.on('upload-success', onUppyFileChanges)
    uppy.on('upload-progress', onUppyFileChanges)

    return () => {
      uppy.off('files-added', handleFilesAdded)
      uppy.off('thumbnail:generated', onTnGenerated)
      uppy.off('upload-success', onUppyFileChanges)
      uppy.off('upload-progress', onUppyFileChanges)
    }
  }, [uppy, onUppyFileChanges])

  return (
    <Box>
      {label && (
        <Typography style={{ fontWeight: 500, marginBottom: 15 }}>
          {label}
        </Typography>
      )}

      {files.map((file, i) => {
        return (
          <FileWrapper style={{ opacity: disableUploadButton ? 0.5 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <FilePreview
                onClick={() => {
                  if (onOpenGalery) {
                    onOpenGalery(
                      files.map((f) => ({
                        url: isUppyFile(f)
                          ? f.preview ?? f.response?.uploadURL
                          : f.url,
                        type: isUppyFile(f) ? f.data.type : f.contentType,
                      })),
                      i,
                    )
                  } else {
                    window.open(
                      isUppyFile(file)
                        ? file.preview ?? URL.createObjectURL(file.data)
                        : file.url,
                      '_blank',
                    )
                  }
                }}
                key={i}
                url={getFilePreview(file)}
                isUploaded={
                  !isUppyFile(file) ? true : !!file.response?.uploadURL
                }
              >
                {isUppyFile(file) && !file.response?.uploadURL && (
                  <ProgressWrapper>
                    {file.progress?.percentage ?? 0} %
                  </ProgressWrapper>
                )}
              </FilePreview>
              {(canRemoveStoredFiles || isUppyFile(file)) && (
                <CloseButton
                  onClick={() => {
                    if (shouldNotConfirmBeforeRemove) {
                      onFileRemoved(file, i)
                    } else {
                      showConfirmPopup({
                        action: () => {
                          onFileRemoved(file, i)
                          closeConfirmModal()
                        },
                      })
                    }
                  }}
                >
                  <ReactSVG src={DeleteIcon} />{' '}
                </CloseButton>
              )}
              {checkIcon && (
                <CheckIcon type={checkIcon}>
                  {checkIcon === 'success' && (
                    <Tooltip title={t('read_picea_checked_success')}>
                      <CheckCircleIcon fontSize="small" />
                    </Tooltip>
                  )}
                  {checkIcon === 'failure' && (
                    <Tooltip title={t('read_picea_checked_failure')}>
                      <ClearIcon fontSize="small" />
                    </Tooltip>
                  )}
                </CheckIcon>
              )}
            </div>
            {!hideFileName && (
              <Typography
                style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}
              >
                {getFilename(file)}
              </Typography>
            )}
          </FileWrapper>
        )
      })}
      {(multiple || replaceOnUpload || files.length === 0) && (
        <FileWrapper>
          <UploadIconWrapper
            onClick={() => {
              if (replaceOnUpload && !multiple) {
                // Make room for the replacement within the single-file limit.
                uppy.cancelAll()
              }
              uppyDashboard.openModal()
            }}
          >
            <AddCircleOutlineIcon id={`${idPrefix}ADD_FILE`} />
          </UploadIconWrapper>
        </FileWrapper>
      )}
    </Box>
  )
}

const isUppyFile = (file: FileArgs): file is UppyFileObject => 'id' in file

const getUppyFilePreview = (file: UppyFileObject) =>
  file.preview ?? (file.data ? URL.createObjectURL(file.data) : null)

const getFilePreview = (file: FileArgs) => {
  if (isUppyFile(file)) {
    return file.data?.type === 'application/pdf'
      ? PDFIcon
      : getUppyFilePreview(file)
  }
  return !file.contentType || file.contentType === 'application/pdf'
    ? PDFIcon
    : file.url
}

const getFilename = (file: FileArgs) => {
  const filename = !isUppyFile(file) ? file.filename : file.name
  return filename && filename.length > 20
    ? filename?.substring(0, 20) + '...'
    : filename
}

export const FilePreview = styled.li<{ url: string; isUploaded?: boolean }>`
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  filter: brightness(100%);
  opacity: ${(props) => (props.isUploaded ? 1 : 0.6)};
  list-style: none;
  border-radius: 5px;
  cursor: pointer;
  width: 80px;
  height: 110px;
`

const UploadIconWrapper = styled.div`
  float: left;
  width: 80px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: 17px;
  &:hover {
    cursor: pointer;
  }
`

const CloseButton = styled.span`
  cursor: pointer;
  z-index: 100;
  margin-left: -17px;
  margin-top: -12px;
  width: 48px;
  height: 48px;
`

const CheckIcon = styled.div<{ type: 'success' | 'failure' }>`
  cursor: pointer;
  bottom: 45px;
  left: 50%;
  transform: translateX(25px);
  position: absolute;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ type }) => type === 'success' && `background: #04d225;`}
  ${({ type }) => type === 'failure' && `background: #f31c0e;`}
`

const FileWrapper = styled.div`
  display: inline-block;
  margin-right: 30px;
`

const ProgressWrapper = styled.div`
  top: 0px;
  right: 0px;
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 14px;
  justify-content: center;
  font-size: 20px;
  color: white;
  font-weight: 600;
  text-shadow: -2px 0px 12px rgba(0, 0, 0, 1);
`
